/**
 * The state this board is actually in today.
 *
 * The NO-AR-NOVA app registration holds `ServiceHealth.Read.All`, but it has no
 * credential attached — no secret, no certificate, no federated identity — so
 * NOVA cannot mint a token and Microsoft Graph is unreachable by design rather
 * than by failure. Rather than an empty board or a red error, this panel says
 * exactly what is missing, who can fix it, and what will appear when they do.
 *
 * It also covers the two other honest-empty cases the backend distinguishes
 * (`permission_denied`, `upstream_error`), so the page never shows data-less
 * panels without an explanation.
 */

import { CloudAlert, Icon, KeyRound, ShieldAlert } from "@norican/icons";
import { Alert, Badge, Card, DescriptionList, EmptyState, Separator } from "@norican/react";

import type { M365ServiceHealth } from "@/lib/api";

import { M365ErrorsAlert } from "./errors-alert";

interface M365SetupPanelProps {
  health: M365ServiceHealth;
}

/** Copy per non-`ok` reason — the board's whole explanation lives here. */
const REASON_COPY = {
  not_configured: {
    title: "Microsoft Graph credential missing",
    lead: "NOVA cannot sign in to Microsoft Graph, so no service-health data can be read.",
  },
  permission_denied: {
    title: "Microsoft Graph refused the request",
    lead: "NOVA has a credential, but the app registration is missing an application permission.",
  },
  upstream_error: {
    title: "Microsoft Graph could not be reached",
    lead: "The credential and permissions look right — the call itself failed.",
  },
} as const;

export function M365SetupPanel({ health }: M365SetupPanelProps) {
  if (health.status_reason === "ok") {
    return null;
  }

  const copy = REASON_COPY[health.status_reason];
  const isConfigProblem = health.status_reason === "not_configured";
  const icon = isConfigProblem
    ? KeyRound
    : health.status_reason === "permission_denied"
      ? ShieldAlert
      : CloudAlert;

  return (
    <Card variant="outline" accent="left" accentTone={isConfigProblem ? "warning" : "danger"}>
      <Card.Body>
        <EmptyState
          icon={<Icon icon={icon} size="lg" />}
          title={copy.title}
          description={
            <span className="text-fg-muted block max-w-2xl text-sm">
              {copy.lead} Nothing on this page is simulated — the panels below stay empty until a
              real answer comes back from Microsoft.
            </span>
          }
        />

        <Separator />

        <div className="flex flex-col gap-4 pt-4">
          <Alert tone={isConfigProblem ? "warning" : "danger"} title="What Microsoft Graph told us">
            {health.status_detail}
          </Alert>

          {health.missing_configuration.length > 0 && (
            <div className="flex flex-col gap-2">
              <p className="text-fg-muted text-xs font-semibold tracking-wide uppercase">
                Environment values still required
              </p>
              <div className="flex flex-wrap gap-2">
                {health.missing_configuration.map((name) => (
                  <Badge key={name} tone="warning" variant="outline" size="sm" shape="rounded">
                    {name}
                  </Badge>
                ))}
              </div>
            </div>
          )}

          {health.missing_scopes.length > 0 && (
            <div className="flex flex-col gap-2">
              <p className="text-fg-muted text-xs font-semibold tracking-wide uppercase">
                Graph permissions not granted
              </p>
              <div className="flex flex-wrap gap-2">
                {health.missing_scopes.map((scope) => (
                  <Badge key={scope} tone="danger" variant="outline" size="sm" shape="rounded">
                    {scope}
                  </Badge>
                ))}
              </div>
            </div>
          )}

          {isConfigProblem && <AdminChecklist />}

          {/* A board can be both unconfigured and partially unreadable, so the
              partial-read detail is nested inside this panel rather than shown
              beside it: one explanation, in cause order — why nothing is shown
              first, then what else went wrong in the same snapshot. No
              `affects` list, because in this state there are no figures on
              screen for a partial read to undermine. */}
          <M365ErrorsAlert errors={health.errors} />
        </div>
      </Card.Body>
    </Card>
  );
}

/** The exact steps an Entra administrator has to take, in order. */
function AdminChecklist() {
  return (
    <div className="flex flex-col gap-3">
      <p className="text-fg-muted text-xs font-semibold tracking-wide uppercase">
        What an Entra administrator must do
      </p>
      <ol className="text-fg flex list-decimal flex-col gap-2 pl-5 text-sm">
        <li>
          Open the <span className="font-medium">NO-AR-NOVA</span> app registration in Microsoft
          Entra ID.
        </li>
        <li>
          Add a credential under <span className="font-medium">Certificates &amp; secrets</span> — a
          client secret, a certificate, or (preferred) a federated credential for the NOVA container
          app&apos;s managed identity.
        </li>
        <li>
          Store the tenant id, client id, and the new secret in NOVA&apos;s environment, then
          restart the backend. NOVA reads them lazily, so nothing else has to change.
        </li>
        <li>
          Optional: grant <span className="font-medium">ServiceMessage.Read.All</span> as well to
          unlock the message centre feed. Service health works without it.
        </li>
      </ol>
      <Separator />
      <DescriptionList
        orientation="vertical"
        columns={2}
        items={[
          { label: "Auth flow", value: "OAuth2 client credentials (app-only)" },
          { label: "Scope requested", value: "https://graph.microsoft.com/.default" },
          { label: "Permission already granted", value: "ServiceHealth.Read.All" },
          { label: "Who can call Graph", value: "The NOVA backend only — never the browser" },
        ]}
      />
    </div>
  );
}
